import { useState, useEffect, useRef, useCallback } from "react"
import api from "../../../services/api"
import toastError from "../../../errors/toastError"

export const useContacts = ({ searchParam, scrollContainerRef = null, canalFilter }) => {
  const [contacts, setContacts] = useState([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const listRef = useRef(null)
  const isResettingRef = useRef(false)

  // 🔄 Reinicia a lista ao alterar o termo de busca ou canal
  useEffect(() => {
    isResettingRef.current = true
    setContacts([])
    setPageNumber(1)
    setHasMore(true)

    // Libera o reset após um pequeno delay
    const timer = setTimeout(() => {
      isResettingRef.current = false
    }, 100)

    return () => clearTimeout(timer)
  }, [searchParam, canalFilter])

  // 📡 Busca contatos com debounce
  useEffect(() => {
    setIsLoadingMore(true)
    let cancel = false

    const fetchContacts = async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: {
            filtro: searchParam || "",
            pagina: pageNumber || 1,
            canalTipo: canalFilter || "",
          },
        })

        if (cancel) return

        setContacts((prev) => (pageNumber === 1 ? data.contatos : [...prev, ...data.contatos]))
        setHasMore(data.hasMore)
      } catch (err) {
        toastError(err)
      } finally {
        setIsLoadingMore(false)
      }
    }

    const timer = setTimeout(fetchContacts, 300)
    return () => {
      cancel = true
      clearTimeout(timer)
    }
  }, [searchParam, pageNumber, canalFilter])

  // ⬇️ Scroll infinito - Bloqueado durante reset
  const loadMoreContacts = useCallback(() => {
    if (isResettingRef.current) return // 🚫 Bloqueia durante reset
    if (hasMore && !isLoadingMore) {
      setPageNumber((prev) => prev + 1)
    }
  }, [hasMore, isLoadingMore, pageNumber])

  const handleScroll = useCallback(() => {
    const list = scrollContainerRef?.current || listRef.current
    if (!list || isLoadingMore) return
    const { scrollTop, scrollHeight, clientHeight } = list
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreContacts()
    }
  }, [isLoadingMore, loadMoreContacts, scrollContainerRef])

  useEffect(() => {
    const list = scrollContainerRef?.current || listRef.current
    if (!list) return
    list.addEventListener("scroll", handleScroll)
    return () => list.removeEventListener("scroll", handleScroll)
  }, [handleScroll, scrollContainerRef])

  return { contacts, isLoadingMore, listRef }
}
